import React, { useState, useMemo } from 'react';
import Styles from './GapsRecommendations.module.scss';
import { FiCheck, FiAlertCircle, FiArrowRight, FiPlus, FiRefreshCw, FiStar, FiUpload, FiLink } from 'react-icons/fi';
import { FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';

const PLATFORM_CONFIG = {
  twitter: { icon: FaTwitter, label: 'Twitter', color: '#1DA1F2', minSamples: 15 },
  linkedin: { icon: FaLinkedin, label: 'LinkedIn', color: '#0A66C2', minSamples: 15 },
  instagram: { icon: FaInstagram, label: 'Instagram', color: '#E4405F', minSamples: 15 },
  youtube: { icon: FaYoutube, label: 'YouTube', color: '#FF0000', minSamples: 10 },
  tiktok: { icon: FaTiktok, label: 'TikTok', color: '#000000', minSamples: 10 },
  website: { icon: () => <span>📝</span>, label: 'Blog', color: '#10B981', minSamples: 10 },
};

const CONTENT_TYPE_GROUPS = {
  announcements: ['announcement', 'launch', 'news'],
  educational: ['how-to', 'tutorial', 'guide', 'tips'],
  storytelling: ['story', 'journey', 'behind-the-scenes', 'case_study'],
  engagement: ['question', 'poll', 'discussion'],
  promotional: ['promotion', 'offer', 'sale', 'ad_copy'],
  thought_leadership: ['opinion', 'hot-take', 'prediction', 'whitepaper'],
};

const GapsRecommendations = ({ brandId, samples = [], stats, onRefresh, selectedPlatforms = [] }) => {
  const [expandedRec, setExpandedRec] = useState(null);

  // Helper function - must be defined before useMemo that uses it
  const formatContentType = (type) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Filter platforms to only those selected by the user (plus website/blog which is always relevant)
  const activePlatforms = useMemo(() => {
    const platforms = selectedPlatforms.length > 0
      ? Object.entries(PLATFORM_CONFIG).filter(([key]) => selectedPlatforms.includes(key) || key === 'website')
      : Object.entries(PLATFORM_CONFIG);
    return Object.fromEntries(platforms);
  }, [selectedPlatforms]);

  // Analyze platform coverage
  const platformAnalysis = useMemo(() => {
    const analysis = {};

    Object.entries(activePlatforms).forEach(([platform, config]) => {
      const count = samples.filter(s => s.platform === platform).length;
      const gap = Math.max(0, config.minSamples - count);
      const percentage = Math.min(100, Math.round((count / config.minSamples) * 100));

      analysis[platform] = {
        ...config,
        count,
        gap,
        percentage,
        status: percentage >= 100 ? 'complete' : percentage >= 50 ? 'partial' : 'missing'
      };
    });

    return analysis;
  }, [samples, activePlatforms]);

  // Analyze content type coverage
  const contentTypeAnalysis = useMemo(() => {
    const covered = new Set();
    const missing = [];

    samples.forEach(sample => {
      const type = sample.content_type?.toLowerCase() || '';
      const tags = sample.tags || [];

      Object.entries(CONTENT_TYPE_GROUPS).forEach(([group, keywords]) => {
        if (keywords.some(kw => type.includes(kw) || tags.some(t => t.toLowerCase().includes(kw)))) {
          covered.add(group);
        }
      });
    });

    Object.keys(CONTENT_TYPE_GROUPS).forEach(group => {
      if (!covered.has(group)) {
        missing.push(group);
      }
    });

    return { covered: Array.from(covered), missing };
  }, [samples]);

  // Generate recommendations
  const recommendations = useMemo(() => {
    const recs = [];

    // Platform recommendations
    Object.entries(platformAnalysis).forEach(([platform, data]) => {
      if (data.gap > 0) {
        recs.push({
          id: `platform_${platform}`,
          priority: data.gap > 10 ? 'high' : 'medium',
          type: 'platform',
          platform,
          title: `Add ${data.gap} more ${data.label} posts`,
          description: `You have ${data.count} samples. Adding more will improve voice accuracy for ${data.label} content.`,
          impact: `+${Math.round(data.gap * 2)}% ${data.label} accuracy`,
          action: data.count > 0 ? 'fetch_more' : 'connect',
          icon: data.icon,
          color: data.color
        });
      }
    });

    // Content type recommendations
    contentTypeAnalysis.missing.forEach(type => {
      recs.push({
        id: `type_${type}`,
        priority: 'medium',
        type: 'content_type',
        title: `Add ${formatContentType(type)} examples`,
        description: `No samples found for this content style. Add examples so AI can learn how you write ${formatContentType(type).toLowerCase()} content.`,
        impact: `AI will learn your ${formatContentType(type).toLowerCase()} style`,
        action: 'upload',
        icon: FiUpload
      });
    });

    // Quality recommendation
    const lowQualityCount = samples.filter(s => s.quality_score && s.quality_score < 0.5).length;
    if (lowQualityCount > 5) {
      recs.push({
        id: 'quality',
        priority: 'low',
        type: 'quality',
        title: 'Review low-engagement content',
        description: `${lowQualityCount} samples have low engagement. Consider removing them or replacing with better performers.`,
        impact: 'Improved content quality baseline',
        action: 'review',
        icon: FiStar
      });
    }

    // Star recommendation
    const starredCount = samples.filter(s => s.starred).length;
    if (starredCount < 5 && samples.length > 10) {
      recs.push({
        id: 'star',
        priority: 'medium',
        type: 'star',
        title: 'Star your top performing content',
        description: 'Mark your best content so AI knows what style to prioritize. This significantly improves output quality.',
        impact: 'AI prioritizes your best content style',
        action: 'star',
        icon: FiStar
      });
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return recs.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [platformAnalysis, contentTypeAnalysis, samples]);

  const renderPlatformBar = (platform, data) => {
    const Icon = data.icon;
    return (
      <div key={platform} className={Styles.platformRow}>
        <div className={Styles.platformInfo}>
          <div className={Styles.platformIcon} style={{ color: data.color }}>
            <Icon />
          </div>
          <span className={Styles.platformName}>{data.label}</span>
        </div>
        <div className={Styles.platformBar}>
          <div
            className={`${Styles.platformFill} ${Styles[data.status]}`}
            style={{ width: `${data.percentage}%` }}
          />
        </div>
        <div className={Styles.platformStats}>
          <span className={Styles.platformCount}>{data.count}</span>
          <span className={Styles.platformTarget}>/ {data.minSamples}</span>
        </div>
        <div className={`${Styles.platformStatus} ${Styles[data.status]}`}>
          {data.status === 'complete' && <FiCheck />}
          {data.status === 'partial' && <FiAlertCircle />}
          {data.status === 'missing' && <FiAlertCircle />}
        </div>
      </div>
    );
  };

  const renderRecommendation = (rec) => {
    const isExpanded = expandedRec === rec.id;
    const Icon = rec.icon;

    return (
      <div
        key={rec.id}
        className={`${Styles.recCard} ${Styles[rec.priority]} ${isExpanded ? Styles.expanded : ''}`}
        onClick={() => setExpandedRec(isExpanded ? null : rec.id)}
      >
        <div className={Styles.recHeader}>
          <div
            className={Styles.recIcon}
            style={rec.color ? { color: rec.color } : {}}
          >
            <Icon />
          </div>
          <div className={Styles.recContent}>
            <h4>{rec.title}</h4>
            <span className={Styles.recImpact}>{rec.impact}</span>
          </div>
          <span className={`${Styles.priorityBadge} ${Styles[rec.priority]}`}>
            {rec.priority === 'high' && 'High Impact'}
            {rec.priority === 'medium' && 'Recommended'}
            {rec.priority === 'low' && 'Optional'}
          </span>
        </div>

        {isExpanded && (
          <div className={Styles.recDetails}>
            <p>{rec.description}</p>
            <button className={Styles.recAction}>
              {rec.action === 'fetch_more' && 'Fetch More Content'}
              {rec.action === 'connect' && 'Connect Platform'}
              {rec.action === 'upload' && 'Upload Content'}
              {rec.action === 'review' && 'Review Content'}
              {rec.action === 'star' && 'Go to Content Library'}
              <FiArrowRight />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.headerText}>
          <h3>Gaps & Recommendations</h3>
          <p>Discover what content to add for better AI training</p>
        </div>
      </div>

      {/* Platform Coverage */}
      <div className={Styles.section}>
        <div className={Styles.sectionHeader}>
          <h4>Platform Coverage</h4>
          <span className={Styles.sectionSubtitle}>
            {Object.values(platformAnalysis).filter(p => p.status === 'complete').length} of {Object.keys(platformAnalysis).length} complete
          </span>
        </div>

        <div className={Styles.platformList}>
          {Object.entries(platformAnalysis)
            .sort((a, b) => b[1].count - a[1].count)
            .map(([platform, data]) => renderPlatformBar(platform, data))}
        </div>
      </div>

      {/* Content Types */}
      <div className={Styles.section}>
        <div className={Styles.sectionHeader}>
          <h4>Content Type Coverage</h4>
        </div>

        <div className={Styles.typeGrid}>
          <div className={Styles.typeColumn}>
            <h5 className={Styles.coveredLabel}>
              <FiCheck /> Covered ({contentTypeAnalysis.covered.length})
            </h5>
            <div className={Styles.typeList}>
              {contentTypeAnalysis.covered.map(type => (
                <span key={type} className={`${Styles.typeTag} ${Styles.covered}`}>
                  {formatContentType(type)}
                </span>
              ))}
              {contentTypeAnalysis.covered.length === 0 && (
                <span className={Styles.noTypes}>No types detected yet</span>
              )}
            </div>
          </div>

          <div className={Styles.typeColumn}>
            <h5 className={Styles.missingLabel}>
              <FiAlertCircle /> Missing ({contentTypeAnalysis.missing.length})
            </h5>
            <div className={Styles.typeList}>
              {contentTypeAnalysis.missing.map(type => (
                <span key={type} className={`${Styles.typeTag} ${Styles.missing}`}>
                  {formatContentType(type)}
                </span>
              ))}
              {contentTypeAnalysis.missing.length === 0 && (
                <span className={Styles.allCovered}>All content types covered!</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className={Styles.section}>
        <div className={Styles.sectionHeader}>
          <h4>Recommendations</h4>
          <span className={Styles.sectionSubtitle}>
            {recommendations.filter(r => r.priority === 'high').length} high impact actions
          </span>
        </div>

        {recommendations.length > 0 ? (
          <div className={Styles.recList}>
            {recommendations.map(rec => renderRecommendation(rec))}
          </div>
        ) : (
          <div className={Styles.allGood}>
            <FiCheck className={Styles.allGoodIcon} />
            <h4>You're all set!</h4>
            <p>Your content training is comprehensive. Keep adding content as you create more.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GapsRecommendations;
